/**
 * Features Accordion Component (Refactored)
 * Handles auto-rotating accordion with timer bars and image switching
 */

import { querySelector, querySelectorAll, addEventListener } from '../utils/dom.js';
import { ACCORDION_CONFIG } from '../utils/constants.js';

export class FeaturesAccordion {
  constructor(selector, options = {}) {
    this.container = typeof selector === 'string' ? querySelector(selector) : selector;

    if (!this.container) {
      console.warn(`FeaturesAccordion: Container not found for selector "${selector}"`);
      return;
    }

    // Configuration
    this.config = {
      ...ACCORDION_CONFIG,
      ...options
    };

    // DOM elements
    this.items = querySelectorAll('.accordion-item', this.container);
    this.section = this.container.closest('section');
    this.images = this.section
      ? querySelectorAll('.accordion-image, .collect-image', this.section)
      : querySelectorAll('.accordion-image, .collect-image');

    // State
    this.currentIndex = 0;
    this.autoRotateEnabled = true;
    this.rotationInterval = null;

    // Bind methods
    this.onItemClick = this.onItemClick.bind(this);

    this.init();
  }

  init() {
    if (this.items.length === 0) {
      console.warn('FeaturesAccordion: No accordion items found');
      return;
    }

    // Set up click handlers for each accordion item
    this.items.forEach((item, index) => {
      addEventListener(item, 'click', () => this.onItemClick(index));
    });

    // Start auto-rotation
    this.startAutoRotation();
  }

  onItemClick(index) {
    // Stop auto-rotation when user manually clicks
    this.stopAutoRotation();
    this.autoRotateEnabled = false;

    // Switch to clicked item with manual mode
    this.switchToItem(index, true);
  }

  switchToItem(index, isManual = false) {
    // Remove active and manual classes from all items and images
    this.items.forEach(item => {
      item.classList.remove(
        this.config.activeClass,
        this.config.manualSelectedClass
      );

      // Remove animation class to reset
      const timerFill = querySelector('.timer-fill', item);
      if (timerFill) {
        timerFill.classList.remove(this.config.animationClass);
        // Force height reset
        timerFill.style.height = '0';
      }
    });

    this.images.forEach(image => {
      image.classList.remove(this.config.activeClass);

      // Stop and reset video if it's a video element
      if (image.tagName === 'VIDEO') {
        image.pause();
        image.currentTime = 0;
      }
    });

    // Add active class to current item and corresponding image
    const currentItem = this.items[index];
    if (!currentItem) return;

    currentItem.classList.add(this.config.activeClass);

    // Add manual-selected class if user clicked
    if (isManual) {
      currentItem.classList.add(this.config.manualSelectedClass);
    }

    // Start timer animation for active item (only if auto-rotating)
    const timerFill = querySelector('.timer-fill', currentItem);

    if (timerFill && !isManual) {
      // Reset and restart animation for auto-rotation
      setTimeout(() => {
        timerFill.style.height = '';
        timerFill.classList.add(this.config.animationClass);
      }, 50);
    }

    // Switch image
    const accordionId = currentItem.dataset.accordion;
    const correspondingImage = querySelector(
      `.accordion-image[data-image="${accordionId}"]`,
      this.section
    );

    if (correspondingImage) {
      correspondingImage.classList.add(this.config.activeClass);

      // Play video if it's a video element
      if (correspondingImage.tagName === 'VIDEO') {
        correspondingImage.play().catch(error => {
          // Handle autoplay restrictions gracefully
          console.warn('Video autoplay failed:', error);
        });
      }
    }

    this.currentIndex = index;
  }

  startAutoRotation() {
    if (!this.autoRotateEnabled) return;

    // Start timer animation for first item
    this.switchToItem(this.currentIndex);

    // Set up interval to rotate items
    this.rotationInterval = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.items.length;
      this.switchToItem(this.currentIndex);
    }, this.config.timerDuration);
  }

  stopAutoRotation() {
    if (this.rotationInterval) {
      clearInterval(this.rotationInterval);
      this.rotationInterval = null;
    }

    // Stop all timer animations
    this.items.forEach(item => {
      const timerFill = querySelector('.timer-fill', item);
      if (timerFill) {
        timerFill.classList.remove(this.config.animationClass);
      }
    });
  }

  // Public method to resume auto-rotation
  resumeAutoRotation() {
    this.autoRotateEnabled = true;
    this.startAutoRotation();
  }

  // Clean up when component is destroyed
  destroy() {
    this.stopAutoRotation();

    // Remove all event listeners by cloning nodes
    this.items.forEach(item => {
      const newItem = item.cloneNode(true);
      if (item.parentNode) {
        item.parentNode.replaceChild(newItem, item);
      }
    });
  }
}
